import { isCartBundleNotFoundError, isCartUpsertConcurrencyError } from '@/lib/cartErrors'
import Sunny16BaseService from '@/services/Sunny16BaseService'

import {
  ApplyPromoCodeStorefrontDto,
  CartBundleUpdateStorefrontDto,
  CartBundleUpsertStorefrontDto,
  CartItemAppointmentCustomizationDto,
  CartStorefrontDto,
  CartStorefrontValidationResultDto,
  PromoCodeResultStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

/**
 * Passed as `externalAdditionalIdentifier` on cart API calls.
 * In cady-commerce this field carries the CadyMarket enum (graduation, underclass, etc.).
 * Booking doesn't map to any of those markets — this constant identifies
 * booking-originating carts in the Sunny16 backend and backoffice.
 */
const BOOKING_PRODUCT_LINE = 'booking'

class Sunny16CommerceCartService extends Sunny16BaseService {
  constructor() {
    super(import.meta.env.VITE_SUNNY_16_CART_API_BASE_URL, 'Sunny16CommerceCartService')
  }
  async getCart(cartId: string, securityKey: string): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.get(`/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async upsertCartBundle(
    inputCartBundle: CartBundleUpsertStorefrontDto,
    cartId?: string,
    securityKey?: string,
    subjectId?: number | null,
    schoolId?: number | null,
    eventDate?: string | null,
    catalogUpsellId?: string | null,
    sourceHint?: string | null
  ): Promise<CartStorefrontDto> {
    try {
      const queryParams = new URLSearchParams()
      if (cartId) queryParams.append('cartId', cartId)
      if (securityKey) queryParams.append('securityKey', securityKey)
      if (subjectId) queryParams.append('externalSubjectIdentifier', subjectId.toString())
      if (schoolId) queryParams.append('externalEventIdentifier', schoolId.toString())
      queryParams.append('externalAdditionalIdentifier', BOOKING_PRODUCT_LINE)
      if (eventDate) queryParams.append('externalEventDate', eventDate.toString())
      if (catalogUpsellId) queryParams.append('catalogUpsellId', catalogUpsellId)
      if (sourceHint) queryParams.append('sourceHint', sourceHint)
      let url
      if (inputCartBundle.cartBundleId) {
        url = `/${inputCartBundle.cartBundleId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      } else {
        url = `/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      }
      const response = await this.axiosInstance.post(url, inputCartBundle, {
        _expectedError: isCartUpsertConcurrencyError
      } as object)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async updateCartBundleQuantity(
    cartId: string,
    securityKey: string,
    cartBundleId: string,
    quantity: number
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.put(
        `/${cartId}/${securityKey}/${cartBundleId}/${quantity}`
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
  // Param names mirror the API contract. The `externalEventIdentifier` slot
  // is what the backend uses to scope a cart to a catalog; in this app the
  // booking catalog is school-scoped, so callers pass a school id here.
  // (Other Sunny16 apps pass an event id, which is why the API field is
  // named `externalEventIdentifier`.)
  async updateCartIdentifiers(
    cartId: string,
    securityKey: string,
    externalSubjectIdentifier?: number | null,
    externalEventIdentifier?: number | null,
    externalAdditionalIdentifier?: string | null
  ): Promise<CartStorefrontDto> {
    try {
      const queryParams = new URLSearchParams()
      if (externalSubjectIdentifier) {
        queryParams.append('externalSubjectIdentifier', externalSubjectIdentifier.toString())
      }
      if (externalEventIdentifier) {
        queryParams.append('externalEventIdentifier', externalEventIdentifier.toString())
      }
      if (externalAdditionalIdentifier) {
        queryParams.append('externalAdditionalIdentifier', externalAdditionalIdentifier)
      }
      const query = queryParams.toString()
      const url = `/identifiers/${cartId}/${securityKey}${query ? `?${query}` : ''}`
      const response = await this.axiosInstance.post(url)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async updateCartConfiguration(
    cartId: string,
    securityKey: string,
    cartConfiguration: CartBundleUpdateStorefrontDto[]
  ) {
    try {
      const response = await this.axiosInstance.post(
        `/config/${cartId}/${securityKey}`,
        cartConfiguration
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
  async upsertCartCustomizations(
    cartId: string,
    securityKey: string,
    cartItemCustomizations: CartItemAppointmentCustomizationDto[]
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.post(
        `/customizations/${cartId}/${securityKey}`,
        cartItemCustomizations
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
  async deleteCartBundle(
    cartId: string,
    securityKey: string,
    cartBundleId: string
  ): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.delete(
        `/${cartId}/${securityKey}/${cartBundleId}`,
        { _expectedError: isCartBundleNotFoundError } as object
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
  async deleteAllCartBundles(cartId: string, securityKey: string): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.delete(`/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async applyPromoCode(
    cartId: string,
    securityKey: string,
    data: ApplyPromoCodeStorefrontDto
  ): Promise<PromoCodeResultStorefrontDto> {
    try {
      const response = await this.axiosInstance.post(`/promotions/${cartId}/${securityKey}`, data)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async prepareCart(cartId: string, securityKey: string): Promise<CartStorefrontDto> {
    try {
      const response = await this.axiosInstance.post(`/prepare/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  async validateCart(
    cartId: string,
    securityKey: string
  ): Promise<CartStorefrontValidationResultDto> {
    try {
      const response = await this.axiosInstance.get(`/validate/${cartId}/${securityKey}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
}

export default Sunny16CommerceCartService
